import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { API, useApp } from "@/contexts/AppContext";
import { Button } from "@/components/ui/button";
import { HeartPulse, Printer, ArrowLeft, Landmark } from "lucide-react";
import { useNavigate } from "react-router-dom";

const CAT_LABEL = { pregnant: "Femmes enceintes", child: "Enfants 0-3 ans", elderly: "Personnes âgées", general: "Général" };
const LANG_LABEL = { fr: "Français", ln: "Lingala", sw: "Swahili", ts: "Tshiluba", kg: "Kikongo" };
const MONTHS_FR = ["janvier", "février", "mars", "avril", "mai", "juin", "juillet", "août", "septembre", "octobre", "novembre", "décembre"];

export default function MinistryReport() {
  const { user } = useApp();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/ministry/stats`)
      .then(r => setData(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Erreur"));
  }, []);

  if (err) return <div className="p-10 text-center text-[#C62828]">{err}</div>;
  if (!data) return <div className="p-10 text-center text-[#5C6B61]">Génération du rapport…</div>;

  const now = new Date(data.generated_at);
  const month = MONTHS_FR[now.getMonth()];
  const year = now.getFullYear();
  const totalDeaths = data.overview.patients_deceased;
  const totalHosp = data.overview.patients_hospitalized;
  const isProvincial = data.scope?.type === "provincial";
  const scopeLabel = isProvincial ? `DPS ${data.scope.province}` : "National — RDC";

  return (
    <div className="min-h-screen bg-white text-[#1A1D1A]" data-testid="ministry-report-page">
      {/* Toolbar - hidden in print */}
      <div className="print:hidden border-b border-[#E5EBE7] px-6 py-3 flex items-center justify-between sticky top-0 bg-white/95 backdrop-blur z-10">
        <Button variant="outline" size="sm" onClick={() => navigate(-1)} data-testid="report-back">
          <ArrowLeft className="w-4 h-4 mr-1.5"/> Retour au tableau de bord
        </Button>
        <div className="text-xs text-[#5C6B61]">
          Rapport officiel · Format A4 · Prêt à imprimer ou enregistrer en PDF (Ctrl+P → "Enregistrer au format PDF")
        </div>
        <Button onClick={() => window.print()} className="bg-[#1E4631] hover:bg-[#163424]" size="sm" data-testid="report-print-btn">
          <Printer className="w-4 h-4 mr-1.5"/> Imprimer / PDF
        </Button>
      </div>

      {/* Report content - A4 page */}
      <div className="max-w-[210mm] mx-auto bg-white">
        {/* ===== PAGE 1 — Cover ===== */}
        <section className="p-16 min-h-[297mm] flex flex-col page-break">
          {/* Republic header */}
          <div className="text-center pb-6 border-b-2 border-[#1E4631]">
            <div className="text-xs uppercase tracking-widest text-[#5C6B61]">
              République Démocratique du Congo
            </div>
            <div className="text-base font-bold mt-1 text-[#1A1D1A]">Ministère de la Santé Publique</div>
            <div className="text-xs text-[#5C6B61] mt-0.5">Direction du Système National d'Information Sanitaire (SNIS)</div>
          </div>

          {/* Cover title */}
          <div className="flex-1 flex flex-col items-center justify-center text-center">
            <div className="w-24 h-24 rounded-2xl bg-[#1E4631] grid place-items-center mb-8">
              <Landmark className="w-14 h-14 text-white"/>
            </div>
            <div className="text-xs uppercase tracking-widest text-[#D96C4A] font-semibold mb-2">
              Rapport officiel · {scopeLabel}
            </div>
            <h1 className="font-[Outfit] font-bold text-4xl leading-tight max-w-xl">
              Bulletin de santé publique
            </h1>
            <p className="text-lg text-[#5C6B61] mt-3">
              Situation épidémiologique et de couverture des soins
            </p>
            <div className="mt-8 text-2xl font-[Outfit] font-semibold text-[#1E4631]">
              {month.charAt(0).toUpperCase() + month.slice(1)} {year}
            </div>
            <div className="text-xs text-[#5C6B61] mt-1">
              Généré le {now.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })} à {now.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}
            </div>
          </div>

          {/* Source & recipient */}
          <div className="grid grid-cols-2 gap-6 text-xs">
            <div className="border border-[#E5EBE7] rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B61] mb-1">Source des données</div>
              <div className="font-semibold text-sm">KWIKILA Care</div>
              <div className="text-[#5C6B61] mt-0.5">Plateforme SaaS de gestion patient</div>
              <div className="text-[#5C6B61]">kwikilacare.com · Kinshasa, RDC</div>
            </div>
            <div className="border border-[#E5EBE7] rounded-lg p-4">
              <div className="text-[10px] uppercase tracking-widest text-[#5C6B61] mb-1">Destinataire du rapport</div>
              <div className="font-semibold text-sm">{user?.name || "Autorité sanitaire"}</div>
              <div className="text-[#5C6B61] mt-0.5">{user?.email}</div>
              {data.user?.province && <div className="text-[#5C6B61]">DPS {data.user.province}</div>}
            </div>
          </div>

          {/* Confidentiality */}
          <div className="text-[9px] text-[#8A9990] text-center mt-6">
            Document confidentiel — Diffusion réservée aux autorités sanitaires et à leurs services autorisés.
            Aucune donnée personnelle identifiante n'est incluse conformément au secret médical.
          </div>
        </section>

        {/* ===== PAGE 2 — Overview ===== */}
        <section className="p-16 min-h-[297mm] page-break">
          <ReportHeader scopeLabel={scopeLabel} pageNum={2} month={month} year={year}/>

          <h2 className="font-[Outfit] font-bold text-2xl mt-6 mb-1">1. Résumé exécutif</h2>
          <div className="w-16 h-1 bg-[#D96C4A] rounded mb-6"/>

          <p className="text-sm leading-relaxed text-[#3F4A44] mb-6">
            Ce bulletin présente l'état de la couverture sanitaire et la situation épidémiologique observés
            par le réseau KWIKILA Care sur la {isProvincial ? `province du ${data.scope.province}` : "totalité du territoire national"} au {now.toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}.
          </p>

          <h3 className="font-[Outfit] font-semibold text-sm uppercase tracking-widest text-[#1E4631] mt-8 mb-3">Indicateurs clés</h3>
          <div className="grid grid-cols-2 gap-4">
            <KpiBox label="Hôpitaux partenaires actifs" value={data.overview.hospitals_active} sub={`sur ${data.overview.hospitals_total} inscrits`}/>
            <KpiBox label="Patients suivis" value={data.overview.patients_total.toLocaleString("fr-FR")} sub={`dont ${data.overview.patients_active} en suivi actif`}/>
            <KpiBox label="Hospitalisations en cours" value={totalHosp} accent="orange"/>
            <KpiBox label="Décès enregistrés (cumul)" value={totalDeaths} accent="red"/>
            <KpiBox label="Rendez-vous programmés" value={data.overview.appointments_total.toLocaleString("fr-FR")}/>
            <KpiBox label="Rappels SMS envoyés" value={data.overview.sms_total.toLocaleString("fr-FR")}/>
          </div>

          <h3 className="font-[Outfit] font-semibold text-sm uppercase tracking-widest text-[#1E4631] mt-8 mb-3">Populations vulnérables suivies</h3>
          <div className="grid grid-cols-4 gap-3">
            <KpiBox compact label="Femmes enceintes" value={data.by_category?.pregnant || 0}/>
            <KpiBox compact label="Enfants 0-3 ans" value={data.by_category?.child || 0}/>
            <KpiBox compact label="Personnes âgées" value={data.by_category?.elderly || 0}/>
            <KpiBox compact label="Général" value={data.by_category?.general || 0}/>
          </div>
        </section>

        {/* ===== PAGE 3 — Morbidity / Mortality ===== */}
        <section className="p-16 min-h-[297mm] page-break">
          <ReportHeader scopeLabel={scopeLabel} pageNum={3} month={month} year={year}/>
          <h2 className="font-[Outfit] font-bold text-2xl mt-6 mb-1">2. Morbidité & mortalité</h2>
          <div className="w-16 h-1 bg-[#D96C4A] rounded mb-6"/>

          <h3 className="font-[Outfit] font-semibold text-sm uppercase tracking-widest text-[#1E4631] mb-3">2.1 Décès par tranche d'âge</h3>
          <ReportTable
            headers={["Catégorie", "Nombre de décès", "Part relative"]}
            rows={Object.entries(data.deaths_by_category || {}).map(([k, v]) => [
              CAT_LABEL[k] || k,
              String(v),
              totalDeaths > 0 ? `${Math.round((v / totalDeaths) * 100)} %` : "—",
            ])}
            footer={["TOTAL", String(totalDeaths), "100 %"]}
          />

          <h3 className="font-[Outfit] font-semibold text-sm uppercase tracking-widest text-[#1E4631] mt-8 mb-3">2.2 Principales causes de décès</h3>
          {(data.death_causes || []).length === 0 ? (
            <p className="text-sm italic text-[#5C6B61]">Aucune cause enregistrée sur la période.</p>
          ) : (
            <ReportTable
              headers={["Cause de décès", "Nombre de cas", "Part relative"]}
              rows={data.death_causes.map(c => [
                c.cause,
                String(c.count),
                totalDeaths > 0 ? `${Math.round((c.count / totalDeaths) * 100)} %` : "—",
              ])}
            />
          )}

          <h3 className="font-[Outfit] font-semibold text-sm uppercase tracking-widest text-[#1E4631] mt-8 mb-3">2.3 Hospitalisations en cours par tranche d'âge</h3>
          <ReportTable
            headers={["Catégorie", "Hospitalisations", "Part relative"]}
            rows={Object.entries(data.hospitalized_by_category || {}).map(([k, v]) => [
              CAT_LABEL[k] || k,
              String(v),
              totalHosp > 0 ? `${Math.round((v / totalHosp) * 100)} %` : "—",
            ])}
            footer={["TOTAL", String(totalHosp), "100 %"]}
          />
        </section>

        {/* ===== PAGE 4 — Coverage / Trend ===== */}
        <section className="p-16 min-h-[297mm] page-break">
          <ReportHeader scopeLabel={scopeLabel} pageNum={4} month={month} year={year}/>
          <h2 className="font-[Outfit] font-bold text-2xl mt-6 mb-1">3. Couverture & tendances</h2>
          <div className="w-16 h-1 bg-[#D96C4A] rounded mb-6"/>

          <h3 className="font-[Outfit] font-semibold text-sm uppercase tracking-widest text-[#1E4631] mb-3">3.1 Évolution mensuelle (6 derniers mois)</h3>
          <ReportTable
            headers={["Mois", "Nouveaux patients", "Hospitalisations", "Décès"]}
            rows={(data.trend_6m || []).map(t => [t.label, String(t.new_patients), String(t.hospitalizations), String(t.deaths)])}
          />

          <h3 className="font-[Outfit] font-semibold text-sm uppercase tracking-widest text-[#1E4631] mt-8 mb-3">3.2 Répartition linguistique des patients</h3>
          <ReportTable
            headers={["Langue préférée", "Nombre de patients"]}
            rows={Object.entries(data.by_language || {})
              .sort((a, b) => b[1] - a[1])
              .map(([k, v]) => [LANG_LABEL[k] || k, String(v)])}
          />

          <h3 className="font-[Outfit] font-semibold text-sm uppercase tracking-widest text-[#1E4631] mt-8 mb-3">3.3 Répartition géographique</h3>
          <div className="grid grid-cols-2 gap-6">
            <div>
              <div className="text-xs font-semibold text-[#5C6B61] mb-2">Hôpitaux par ville</div>
              {(data.by_city || []).length === 0 ? (
                <p className="text-xs italic text-[#5C6B61]">Aucune donnée</p>
              ) : (
                <ReportTable compact headers={["Ville", "Hôpitaux"]} rows={data.by_city.map(c => [c.city, String(c.hospitals)])}/>
              )}
            </div>
            <div>
              <div className="text-xs font-semibold text-[#5C6B61] mb-2">Communes avec le plus de patients</div>
              {(data.by_commune || []).length === 0 ? (
                <p className="text-xs italic text-[#5C6B61]">Aucune donnée renseignée</p>
              ) : (
                <ReportTable compact headers={["Commune", "Patients"]} rows={data.by_commune.map(c => [c.commune, String(c.patients)])}/>
              )}
            </div>
          </div>
        </section>

        {/* ===== PAGE 5 — Recommandations & Signature ===== */}
        <section className="p-16 min-h-[297mm]">
          <ReportHeader scopeLabel={scopeLabel} pageNum={5} month={month} year={year}/>
          <h2 className="font-[Outfit] font-bold text-2xl mt-6 mb-1">4. Recommandations opérationnelles</h2>
          <div className="w-16 h-1 bg-[#D96C4A] rounded mb-6"/>

          <div className="space-y-4 text-sm leading-relaxed text-[#3F4A44]">
            <Reco num="1" title="Renforcement du suivi prénatal">
              Poursuivre l'accompagnement des {data.by_category?.pregnant || 0} femmes enceintes actuellement suivies via des rappels SMS multilingues (français, lingala, swahili, tshiluba, kikongo) pour les consultations prénatales et la vaccination anti-tétanique.
            </Reco>
            <Reco num="2" title="Vaccination infantile 0-3 ans">
              Les {data.by_category?.child || 0} enfants inscrits doivent bénéficier de rappels vaccinaux ciblés (PEV RDC). Un tableau de bord par hôpital permet de suivre les taux de couverture.
            </Reco>
            <Reco num="3" title="Prévention chez les personnes âgées">
              Attention particulière aux {data.by_category?.elderly || 0} personnes âgées suivies : proposer des consultations HTA / diabète régulières via la fonctionnalité RDV automatique.
            </Reco>
            <Reco num="4" title="Extension du réseau">
              Poursuivre l'inscription de nouveaux hôpitaux via kwikilacare.com afin d'améliorer la représentativité géographique {isProvincial ? `dans la province du ${data.scope.province}` : "sur les 26 provinces"}.
            </Reco>
          </div>

          {/* Signature block */}
          <div className="mt-16 grid grid-cols-2 gap-12 pt-8 border-t-2 border-[#1E4631]">
            <div>
              <div className="text-xs uppercase tracking-widest text-[#5C6B61] mb-2">Signature — Autorité sanitaire</div>
              <div className="h-24 border-b border-[#5C6B61]"></div>
              <div className="text-xs mt-2 text-[#5C6B61]">Nom :</div>
              <div className="text-sm font-semibold">{user?.name || "…"}</div>
              {user?.email && <div className="text-xs text-[#5C6B61] mt-0.5">Email : {user.email}</div>}
              <div className="text-xs text-[#5C6B61] mt-2">Fait à …………………………, le {now.toLocaleDateString("fr-FR")}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-widest text-[#5C6B61] mb-2">Fournisseur de données</div>
              <div className="flex items-center gap-3 mt-2">
                <div className="w-12 h-12 rounded-xl bg-[#1E4631] grid place-items-center">
                  <HeartPulse className="w-7 h-7 text-white"/>
                </div>
                <div>
                  <div className="font-[Outfit] font-bold text-base">KWIKILA Care</div>
                  <div className="text-xs text-[#5C6B61]">La santé en confiance · RDC</div>
                </div>
              </div>
              <div className="text-xs text-[#5C6B61] mt-4 space-y-0.5">
                <div>kwikilacare.com</div>
                <div>contact@kwikilacare.com</div>
                <div className="italic mt-2">Données extraites en temps réel de la plateforme au moment de la génération.</div>
              </div>
            </div>
          </div>

          <div className="text-[9px] text-[#8A9990] text-center mt-12 pt-6 border-t border-[#E5EBE7]">
            Rapport confidentiel · KWIKILA Care © {year} · Document interne au Ministère de la Santé Publique de la République Démocratique du Congo.
          </div>
        </section>
      </div>

      {/* Print CSS */}
      <style>{`
        @media print {
          @page { size: A4; margin: 0; }
          html, body { background: #ffffff !important; }
          body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .page-break { page-break-after: always; }
          section { min-height: 297mm; }
        }
      `}</style>
    </div>
  );
}

function ReportHeader({ scopeLabel, pageNum, month, year }) {
  return (
    <div className="flex items-center justify-between pb-3 border-b border-[#E5EBE7] text-xs text-[#5C6B61]">
      <div className="flex items-center gap-2">
        <div className="w-6 h-6 rounded bg-[#1E4631] grid place-items-center">
          <HeartPulse className="w-3.5 h-3.5 text-white"/>
        </div>
        <span className="font-semibold text-[#1A1D1A]">KWIKILA Care</span>
        <span>·</span>
        <span>Bulletin santé publique {scopeLabel}</span>
        <span>·</span>
        <span>{month.charAt(0).toUpperCase() + month.slice(1)} {year}</span>
      </div>
      <div>Page {pageNum} / 5</div>
    </div>
  );
}

function KpiBox({ label, value, sub, accent, compact }) {
  const color = accent === "red" ? "#C62828" : accent === "orange" ? "#D96C4A" : "#1E4631";
  return (
    <div className={`border border-[#E5EBE7] rounded-lg ${compact ? "p-3" : "p-4"}`}>
      <div className="text-[10px] uppercase tracking-widest text-[#5C6B61]">{label}</div>
      <div className={`font-[Outfit] font-bold mt-1 ${compact ? "text-xl" : "text-3xl"}`} style={{ color }}>{value}</div>
      {sub && <div className="text-[10px] text-[#5C6B61] mt-0.5">{sub}</div>}
    </div>
  );
}

function ReportTable({ headers, rows, footer, compact }) {
  return (
    <table className="w-full border-collapse text-sm" style={{ tableLayout: "auto" }}>
      <thead>
        <tr className="bg-[#1E4631] text-white">
          {headers.map((h, i) => (
            <th key={i} className={`text-left ${compact ? "px-2 py-1.5 text-xs" : "px-3 py-2"} font-semibold border-r border-white/10 last:border-r-0`}>{h}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className={i % 2 === 0 ? "bg-white" : "bg-[#FDFBF7]"}>
            {r.map((cell, j) => (
              <td key={j} className={`${compact ? "px-2 py-1.5 text-xs" : "px-3 py-2"} border-b border-[#E5EBE7]`}>{cell}</td>
            ))}
          </tr>
        ))}
        {footer && (
          <tr className="bg-[#1E4631]/10 font-bold">
            {footer.map((cell, j) => (
              <td key={j} className={`${compact ? "px-2 py-1.5 text-xs" : "px-3 py-2"}`}>{cell}</td>
            ))}
          </tr>
        )}
      </tbody>
    </table>
  );
}

function Reco({ num, title, children }) {
  return (
    <div className="flex gap-4">
      <div className="w-8 h-8 rounded-full bg-[#D96C4A] text-white font-bold grid place-items-center shrink-0">{num}</div>
      <div>
        <div className="font-semibold text-sm text-[#1E4631]">{title}</div>
        <div className="text-sm mt-1 text-[#3F4A44]">{children}</div>
      </div>
    </div>
  );
}
